import { Avatar } from "@coworker/apprestructured/src/shared/components/Avatar/Avatar";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import {
  CountryEmptyTag,
  CountryNameCell,
  CountryTag,
  CountryTags,
  EmptyState,
  HeaderRow,
  PageInfo,
  PaginationBar,
  RoleCell,
  RoleCheck,
  SortButton,
  UserEmail,
  UserList,
  UserName,
  UserRow,
} from "./AtkomstUsersPage.styles";
import {
  AtkomstUser,
  SortCol,
  getUserCountryNames,
  isCoworker,
  isCountrySU,
  isStoreSU,
} from "../utils/atkomstUsersUtils";

interface Props {
  paginatedUsers: AtkomstUser[];
  displayNames: Record<string, string> | undefined;
  countryNameById: Map<string, string>;
  storeRefToCountryId: Map<string, string>;
  filteredCount: number;
  sortCol: SortCol | null;
  sortDir: "asc" | "desc";
  onCycleSort: (col: SortCol) => void;
  safePage: number;
  totalPages: number;
  onSetPage: (updater: (p: number) => number) => void;
}

export const UsersTable = ({
  paginatedUsers,
  displayNames,
  countryNameById,
  storeRefToCountryId,
  filteredCount,
  sortCol,
  sortDir,
  onCycleSort,
  safePage,
  totalPages,
  onSetPage,
}: Props) => {
  const sortIndicator = (col: SortCol) =>
    sortCol === col ? (sortDir === "asc" ? "▲" : "▼") : "↕";

  return (
    <>
      <UserList>
        <HeaderRow>
          <span />
          <span>User</span>
          <SortButton
            type="button"
            onClick={() => onCycleSort("countryName")}
            aria-label="Sort by Country Name"
          >
            Country Name <span aria-hidden>{sortIndicator("countryName")}</span>
          </SortButton>
          <SortButton
            type="button"
            onClick={() => onCycleSort("store")}
            aria-label="Sort by Store Super User"
          >
            Store SU <span aria-hidden>{sortIndicator("store")}</span>
          </SortButton>
          <SortButton
            type="button"
            onClick={() => onCycleSort("country")}
            aria-label="Sort by Country Super User"
          >
            Country SU <span aria-hidden>{sortIndicator("country")}</span>
          </SortButton>
          <SortButton
            type="button"
            onClick={() => onCycleSort("coworker")}
            aria-label="Sort by Coworker"
          >
            Coworker <span aria-hidden>{sortIndicator("coworker")}</span>
          </SortButton>
        </HeaderRow>

        {paginatedUsers.map((user) => {
          const displayName = displayNames?.[user.email] ?? "";
          const userCountryNames = getUserCountryNames(
            user,
            countryNameById,
            storeRefToCountryId
          );
          return (
            <UserRow key={user.email}>
              <Avatar isBigImage={false} uid={user.email} />
              <div>
                <UserName>{displayName || user.email}</UserName>
                {displayName && <UserEmail>{user.email}</UserEmail>}
              </div>
              <CountryNameCell>
                <CountryTags>
                  {userCountryNames.length ? (
                    userCountryNames.map((name) => (
                      <CountryTag key={`${user.email}-${name}`}>
                        {name}
                      </CountryTag>
                    ))
                  ) : (
                    <CountryEmptyTag>No country</CountryEmptyTag>
                  )}
                </CountryTags>
              </CountryNameCell>
              <RoleCell>
                {isStoreSU(user) && <RoleCheck $color="#0A8A3C">✓</RoleCheck>}
              </RoleCell>
              <RoleCell>
                {isCountrySU(user) && <RoleCheck $color="#C2410C">✓</RoleCheck>}
              </RoleCell>
              <RoleCell>
                {isCoworker(user) && <RoleCheck $color="#0058A3">✓</RoleCheck>}
              </RoleCell>
            </UserRow>
          );
        })}
      </UserList>

      {filteredCount === 0 && (
        <EmptyState>No users match the current filters.</EmptyState>
      )}

      {totalPages > 1 && (
        <PaginationBar>
          <FixaButton
            type="secondary"
            size="small"
            disabled={safePage <= 1}
            onClick={() => onSetPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </FixaButton>
          <PageInfo>
            Page {safePage} of {totalPages}
          </PageInfo>
          <FixaButton
            type="secondary"
            size="small"
            disabled={safePage >= totalPages}
            onClick={() => onSetPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </FixaButton>
        </PaginationBar>
      )}
    </>
  );
};
